import React, { useState, useEffect } from "react";
import Modal from "../../../../utils/components/Modal";
import Button from "../../../../utils/components/Button";
import LoadingOverlay from "../../../../utils/components/LoadingOverlay";

const FinancialModal = ({
    show,
    onClose,
    account,
    financials,
    invoices,
    siloBalances,
    priceList = [],
    loading = {},
    applyPriceItem,
    registerInvoicesPayment,
    registerAddBalance,
    registerPlayerInvoicesPayment,
    registerPlayerAddBalance,
    applyPlayerPriceItem,
    createPlayerDebt,
    // New Props for Origin Tracking
    organizerId,
    matches = [],
    sanctions = [],
    loadMatches,
    loadSanctions,
    createDebt,
}) => {
    const [modalStep, setModalStep] = useState(0); // 0: Dashboard, 1: Add Debt, 2: Payment, 3: Manual Debt
    const [selectedInvoiceIds, setSelectedInvoiceIds] = useState([]);
    const [paymentMode, setPaymentMode] = useState("invoices"); // "invoices" or "balance"
    const [balanceAmount, setBalanceAmount] = useState("");
    const [balanceDescription, setBalanceDescription] = useState("");
    const [paymentMethod, setPaymentMethod] = useState("cash");
    const [selectedPriceSku, setSelectedPriceSku] = useState("");

    // Manual Debt & Origin State
    const [isManualDebt, setIsManualDebt] = useState(false);
    const [manualDebtData, setManualDebtData] = useState({ amount: "", description: "", type: "custom-fee" });
    const [selectedOrigin, setSelectedOrigin] = useState({ id: "", type: "", name: "" });

    useEffect(() => {
        if (show) {
            setModalStep(0);
            setSelectedInvoiceIds([]);
            setPaymentMode("invoices");
            setBalanceAmount("");
            setBalanceDescription("");
            setSelectedPriceSku("");
            setIsManualDebt(false);
            setManualDebtData({ amount: "", description: "", type: "custom-fee" });
            setSelectedOrigin({ id: "", type: "", name: "" });
        }
    }, [show]);

    useEffect(() => {
        const tournamentId = account?.tournament_id || account?.accountable?.tournament_id;
        if (show && tournamentId && organizerId) {
            if (loadMatches) loadMatches(organizerId, tournamentId);
            if (loadSanctions) loadSanctions(0, 100, organizerId, tournamentId);
            // Assuming loadPriceList is also a prop if it's used here
            // if (loadPriceList && (!priceList || priceList.length === 0)) {
            //     loadPriceList(tournamentId);
            // }
        }
    }, [show, account, organizerId, priceList, loadMatches, loadSanctions]); // Added dependencies for useEffect

    // Auto-detect origin type based on SKU/Type
    useEffect(() => {
        if (selectedPriceSku) {
            const item = priceList.find(p => p.sku === selectedPriceSku);
            if (item) {
                if (item.type === 'tournament-entry-fee') {
                    setSelectedOrigin({
                        id: account?.tournament_id || account?.accountable?.tournament_id,
                        type: 'Tournament',
                        name: account?.tournament_name || "Torneo"
                    });
                } else if (item.type === 'match-fee') {
                    setSelectedOrigin({ id: "", type: 'Match', name: "" });
                } else if (['yellow-card-fee', 'red-card-fee', 'double-yellow-card-fee', 'sanction-fee'].includes(item.type)) {
                    setSelectedOrigin({ id: "", type: 'Incidence', name: "" });
                } else {
                    setSelectedOrigin({ id: "", type: "", name: "" });
                }
            }
        }
    }, [selectedPriceSku, priceList, account]);

    const formatBalance = (balance) => {
        if (typeof balance === "object" && balance !== null) {
            return Number(balance.amount) || 0;
        }
        return Number(balance) || 0;
    };

    const formatCurrency = (value) => {
        const number = formatBalance(value);
        return number.toLocaleString("de-DE", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    };

    const getTypeLabel = (type) => {
        const labels = {
            'match-fee': 'Derecho a partido',
            'tournament-entry-fee': 'Inscripción',
            'sanction-fee': 'Sanción',
            'yellow-card-fee': 'Tarjeta Amarilla',
            'red-card-fee': 'Tarjeta Roja',
            'double-yellow-card-fee': 'Doble Amarilla',
            'protest-fee': 'Protesta',
            'social-club-fee': 'Club Social',
            'custom-fee': 'Cargo personalizado'
        };
        return labels[type] || type || 'Cargo';
    };

    const handleToggleInvoice = (id) => {
        setSelectedInvoiceIds(prev =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    };

    const handleRegisterPayment = () => {
        const isPlayer = account?.type === 'player' || account?.type === 'user';
        const expectedLineupId = account?.lineup_id || account?.accountable_id || (isPlayer ? account?.external_entity_id : undefined);
        const expectedExternalId = isPlayer
            ? (account?.target_id || account?.external_individual_id || account?.account_id || account?.id)
            : (account?.accountable?.external_entity_id || account?.external_entity_id || account?.id);

        if (paymentMode === "invoices") {
            if (selectedInvoiceIds.length === 0) return;
            if (isPlayer) {
                registerPlayerInvoicesPayment(expectedLineupId, expectedExternalId, {
                    invoice_ids: selectedInvoiceIds,
                    method: paymentMethod
                });
            } else {
                registerInvoicesPayment(expectedExternalId, {
                    invoice_ids: selectedInvoiceIds,
                    method: paymentMethod
                });
            }
        } else {
            if (!balanceAmount) return;
            if (isPlayer) {
                registerPlayerAddBalance(expectedLineupId, expectedExternalId, {
                    amount: parseFloat(balanceAmount),
                    description: balanceDescription,
                    method: paymentMethod
                });
            } else {
                registerAddBalance(expectedExternalId, {
                    amount: parseFloat(balanceAmount),
                    description: balanceDescription,
                    method: paymentMethod
                });
            }
        }
        onClose();
        setBalanceAmount("");
        setBalanceDescription("");
    };

    const handleApplyPrice = () => {
        const isPlayer = account?.type === 'player' || account?.type === 'user';
        const expectedLineupId = account?.lineup_id || account?.accountable_id || (isPlayer ? account?.external_entity_id : undefined);
        const expectedExternalId = isPlayer
            ? (account?.target_id || account?.external_individual_id || account?.account_id || account?.id)
            : (account?.accountable?.external_entity_id || account?.external_entity_id || account?.id);

        console.log("account", account);
        if (isManualDebt) {
            if (!manualDebtData.amount || !manualDebtData.description) return;
            const data = {
                amount: Number(manualDebtData.amount),
                description: manualDebtData.description,
                type: manualDebtData.type,
                origin: selectedOrigin?.id ? selectedOrigin : null
            };
            if (isPlayer) {
                createPlayerDebt(expectedLineupId, expectedExternalId, data);
            } else {
                createDebt(expectedExternalId, data);
            }
        } else {
            if (!selectedPriceSku) return;
            if (isPlayer) {
                applyPlayerPriceItem(expectedLineupId, expectedExternalId, selectedPriceSku, selectedOrigin?.id ? selectedOrigin : null);
            } else {
                applyPriceItem(expectedExternalId, selectedPriceSku, selectedOrigin?.id ? selectedOrigin : null);
            }
        }

        setModalStep(0);
        setSelectedPriceSku("");
        setIsManualDebt(false);
    };

    const availableBalance = formatBalance(financials?.balance?.available || financials?.view_balance?.available_balance);

    return (
        <Modal
            show={show}
            handleShow={onClose}
            title={<span><i className="fa fa-bank m-r-xs"></i> Panel Financiero: {account?.name}</span>}
            size="modal-md"
            buttons={
                <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                    {modalStep > 0 ? (
                        <Button
                            className="btn btn-white"
                            title="Volver"
                            onClick={() => setModalStep(0)}
                        />
                    ) : (
                        <div />
                    )}
                    <Button
                        className="btn btn-white"
                        title="Cerrar"
                        onClick={onClose}
                    />
                </div>
            }
            body={
                <LoadingOverlay loading={loading.loadFinancials || loading.loadPlayerFinancials}>
                <div>
                    {/* Modal Header/Summary */}
                    <div style={{
                        backgroundColor: availableBalance === 0 ? '#bdc3c7' : (availableBalance < 0 ? '#ed5565' : '#1ab394'),
                        color: 'white',
                        borderRadius: '12px',
                        padding: '20px',
                        marginBottom: '20px',
                        textAlign: 'center',
                        transition: 'background-color 0.3s'
                    }}>
                        <small style={{ opacity: 0.9 }}>
                            {availableBalance < 0 ? 'Saldo a cobrar' : (availableBalance > 0 ? 'Saldo a favor' : 'Sin saldo pendiente')}
                        </small>
                        <h1 className="no-margins" style={{ fontWeight: 'bold' }}>
                            ${formatCurrency(Math.abs(availableBalance))}
                        </h1>
                    </div>

                    {modalStep === 0 ? (
                        /* Step 0: Dashboard */
                        <div>
                            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                <div className="m-b-sm">
                                    <h4 style={{ fontWeight: 'bold' }}>Deudas pendientes (Facturas)</h4>
                                    <small className="text-muted">Selecciona las facturas que deseas pagar.</small>
                                </div>
                                {(invoices || []).filter(inv => inv.status === 'open').map((invoice, idx) => (
                                    <div key={idx} style={{
                                        backgroundColor: 'white',
                                        borderRadius: '10px',
                                        border: '1px solid #f3f3f4',
                                        padding: '15px',
                                        marginBottom: '10px',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        cursor: 'pointer'
                                    }} onClick={() => handleToggleInvoice(invoice.id)}>
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <div style={{ marginRight: '15px' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedInvoiceIds.includes(invoice.id)}
                                                    readOnly
                                                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                                />
                                            </div>
                                            <div>
                                                <h5 className="no-margins" style={{ fontWeight: 'bold' }}>{invoice.description || "Factura"}</h5>
                                                <small className="text-muted">
                                                    <i className="fa fa-calendar m-r-xs"></i>
                                                    {invoice.created_at ? new Date(invoice.created_at).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }) : "N/A"}
                                                    {invoice.subscribable_type && <span className="label label-xs m-l-sm" style={{ backgroundColor: '#f3f3f4', color: '#676a6c' }}>{getTypeLabel(invoice.subscribable_type)}</span>}
                                                </small>
                                            </div>
                                        </div>
                                        <h4 className="no-margins" style={{ fontWeight: 'bold' }}>
                                            $ {formatCurrency(invoice.final_amount)}
                                        </h4>
                                    </div>
                                ))}

                                {(invoices || []).filter(inv => inv.status === 'open').length === 0 && (
                                    <div className="text-center p-lg">
                                        <p className="text-muted">No hay facturas pendientes.</p>
                                    </div>
                                )}

                                {/* Transactions as History */}
                                <div className="m-t-lg">
                                    <h5 className="text-muted" style={{ borderTop: '1px solid #f3f3f4', paddingTop: '15px' }}>Historial de movimientos</h5>
                                    <div style={{ opacity: 0.7 }}>
                                        {(financials?.transactions || []).slice(0, 5).map((trans, idx) => (
                                            <div key={idx} className="small m-b-xs" style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <span>{trans.description}</span>
                                                <span style={{ color: formatBalance(trans.amount) < 0 ? '#ed5565' : '#1ab394' }}>
                                                    {formatBalance(trans.amount) < 0 ? '-' : '+'} ${formatCurrency(Math.abs(formatBalance(trans.amount)))}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Player Accounts Section */}
                            {(siloBalances || []).filter(s => s.type === 'individual').length > 0 && (
                                <div className="m-t-lg">
                                    <h5 className="text-muted" style={{ borderTop: '1px solid #f3f3f4', paddingTop: '15px' }}>Saldos de jugadores</h5>
                                    <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                        {(siloBalances || []).filter(s => s.type === 'individual').map((playerAcc, idx) => {
                                            const balance = playerAcc.balance?.available || 0;
                                            return (
                                                <div key={idx} style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    padding: '10px 0',
                                                    borderBottom: '1px solid #f3f3f4'
                                                }}>
                                                    <span>{playerAcc.target_name}</span>
                                                    <span style={{
                                                        fontWeight: 'bold',
                                                        color: balance === 0 ? '#333' : (balance < 0 ? '#ed5565' : '#1ab394')
                                                    }}>
                                                        {balance > 0 ? '+' : ''} ${formatCurrency(balance)}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Centered Actions */}
                            <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', marginTop: '20px' }}>
                                <Button
                                    className="btn btn-outline btn-primary"
                                    title={<span><i className="fa fa-plus m-r-xs"></i> Nueva Deuda</span>}
                                    onClick={() => setModalStep(1)}
                                    style={{ borderRadius: '25px', padding: '10px 20px', fontWeight: '600' }}
                                />
                                <Button
                                    className="btn btn-outline btn-success"
                                    title={<span><i className="fa fa-credit-card m-r-xs"></i> Cargar Saldo</span>}
                                    onClick={() => {
                                        setPaymentMode("balance");
                                        setModalStep(2);
                                    }}
                                    style={{ borderRadius: '25px', padding: '10px 20px', fontWeight: '600' }}
                                />
                                <Button
                                    className="btn btn-primary"
                                    title={<span>Cobrar Seleccionadas ({selectedInvoiceIds.length}) <i className="fa fa-chevron-right m-l-xs"></i></span>}
                                    onClick={() => {
                                        setPaymentMode("invoices");
                                        setModalStep(2);
                                    }}
                                    disabled={selectedInvoiceIds.length === 0}
                                    style={{ borderRadius: '25px', padding: '10px 20px', fontWeight: '600', backgroundColor: '#1ab394', border: 'none' }}
                                />
                            </div>
                        </div>
                    ) : modalStep === 1 ? (
                        /* Step 1: Add Debt */
                        <div>
                            <div className="m-b-md">
                                <h4 style={{ fontWeight: 'bold' }}>Agregar deuda</h4>
                                <p className="text-muted">Selecciona una plantilla de la lista de precios para aplicar un cargo.</p>
                            </div>

                            <div className="panel panel-default" style={{ borderRadius: '10px', border: '1px solid #f3f3f4' }}>
                                <div className="panel-body">
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                        <h5 className="no-margins">Aplicar Cargo / Consumo</h5>
                                        <button
                                            className="btn btn-link btn-xs"
                                            onClick={() => setIsManualDebt(!isManualDebt)}
                                            style={{ textDecoration: 'underline' }}
                                        >
                                            {isManualDebt ? "Usar plantillas" : "Concepto libre (Manual)"}
                                        </button>
                                    </div>

                                    {!isManualDebt ? (
                                        <div className="row">
                                            <div className="col-md-12 m-b-sm">
                                                <select
                                                    className="form-control"
                                                    value={selectedPriceSku}
                                                    onChange={(e) => setSelectedPriceSku(e.target.value)}
                                                    style={{ backgroundColor: '#f3f3f4', border: 'none', height: '40px' }}
                                                >
                                                    <option value="">Seleccionar plantilla...</option>
                                                    {(priceList || [])
                                                        .filter(item => {
                                                            const isPlayer = account?.type === 'player';
                                                            const individualTypes = ['sanction-fee', 'yellow-card-fee', 'red-card-fee', 'double-yellow-card-fee', 'social-club-fee', 'protest-fee'];
                                                            const teamTypes = ['tournament-entry-fee', 'match-fee'];

                                                            if (isPlayer) {
                                                                return !teamTypes.includes(item.type);
                                                            } else {
                                                                return !individualTypes.includes(item.type);
                                                            }
                                                        })
                                                        .map(item => (
                                                            <option key={item.sku} value={item.sku}>
                                                                {item.name.toUpperCase()} - $ {formatCurrency(item.amount)}
                                                            </option>
                                                        ))}
                                                </select>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="row">
                                            <div className="col-md-4 m-b-sm">
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    placeholder="Monto"
                                                    value={manualDebtData.amount}
                                                    onChange={(e) => setManualDebtData({ ...manualDebtData, amount: e.target.value })}
                                                    style={{ backgroundColor: '#f3f3f4', border: 'none' }}
                                                />
                                            </div>
                                            <div className="col-md-8 m-b-sm">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Descripción"
                                                    value={manualDebtData.description}
                                                    onChange={(e) => setManualDebtData({ ...manualDebtData, description: e.target.value })}
                                                    style={{ backgroundColor: '#f3f3f4', border: 'none' }}
                                                />
                                            </div>
                                            <div className="col-md-12 m-b-sm">
                                                <select
                                                    className="form-control"
                                                    value={manualDebtData.type}
                                                    onChange={(e) => setManualDebtData({ ...manualDebtData, type: e.target.value })}
                                                    style={{ backgroundColor: '#f3f3f4', border: 'none' }}
                                                >
                                                    <option value="match-fee">Derecho a partido</option>
                                                    <option value="tournament-entry-fee">Inscripción</option>
                                                    <option value="sanction-fee">Sanción</option>
                                                    <option value="yellow-card-fee">Tarjeta amarilla</option>
                                                    <option value="red-card-fee">Tarjeta roja</option>
                                                    <option value="double-yellow-card-fee">Doble tarjeta amarilla</option>
                                                    <option value="protest-fee">Protesta</option>
                                                    <option value="social-club-fee">Club social</option>
                                                    <option value="custom-fee">Tipo personalizado</option>
                                                </select>
                                            </div>
                                        </div>
                                    )}

                                    {/* Origin Selector */}
                                    {(selectedOrigin.type || isManualDebt) && (
                                        <div className="row m-t-sm">
                                            <div className="col-md-12">
                                                <label className="small text-muted">Origen del cargo ({selectedOrigin.type || "Opcional"})</label>
                                                {selectedOrigin.type === 'Tournament' ? (
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={selectedOrigin.name}
                                                        disabled
                                                        style={{ backgroundColor: '#f3f3f4', border: 'none' }}
                                                    />
                                                ) : selectedOrigin.type === 'Match' || (isManualDebt && manualDebtData.type === 'match-fee') ? (
                                                    <select
                                                        className="form-control"
                                                        value={selectedOrigin.id}
                                                        onChange={(e) => {
                                                            const match = matches.find(m => m.id === e.target.value);
                                                            setSelectedOrigin({
                                                                id: e.target.value,
                                                                type: 'Match',
                                                                name: match ? `${match.localTeam?.team?.name || match.localTeam?.name || match.localTeam} vs ${match.visitingTeam?.team?.name || match.visitingTeam?.name || match.visitingTeam}` : ""
                                                            });
                                                        }}
                                                        style={{ backgroundColor: '#f3f3f4', border: 'none' }}
                                                    >
                                                        <option value="">Seleccionar partido...</option>
                                                        {matches.map(m => {
                                                            const localName = m.localTeam?.team?.name || m.localTeam?.name || m.localTeam || "Local";
                                                            const visitingName = m.visitingTeam?.team?.name || m.visitingTeam?.name || m.visitingTeam || "Visitante";
                                                            return (
                                                                <option key={m.id} value={m.id}>
                                                                    {m.datetime || ""} - {localName} vs {visitingName}
                                                                </option>
                                                            );
                                                        })}
                                                    </select>
                                                ) : (selectedOrigin.type === 'Incidence' || (isManualDebt && ['yellow-card-fee', 'red-card-fee', 'double-yellow-card-fee', 'sanction-fee'].includes(manualDebtData.type))) ? (
                                                    <select
                                                        className="form-control"
                                                        value={selectedOrigin.id}
                                                        onChange={(e) => {
                                                            const s = sanctions.find(s => s.id === e.target.value);
                                                            setSelectedOrigin({
                                                                id: e.target.value,
                                                                type: 'Incidence',
                                                                name: s ? `Sanción: ${s.player?.team?.name || s.player?.name || s.player || s.id}` : ""
                                                            });
                                                        }}
                                                        style={{ backgroundColor: '#f3f3f4', border: 'none' }}
                                                    >
                                                        <option value="">Seleccionar sanción/incidencia...</option>
                                                        {sanctions.map(s => {
                                                            const playerName = s.player?.team?.name || s.player?.name || s.player || 'Sanción';
                                                            return (
                                                                <option key={s.id} value={s.id}>
                                                                    {playerName} - {s.comments || s.id}
                                                                </option>
                                                            );
                                                        })}
                                                    </select>
                                                ) : isManualDebt ? (
                                                    <p className="small text-muted italic">El origen se asignará automáticamente al torneo si aplica.</p>
                                                ) : null}
                                            </div>
                                        </div>
                                    )}

                                    <div className="row m-t-md">
                                        <div className="col-md-12">
                                            <Button
                                                className="btn btn-primary btn-block"
                                                title="Aplicar Cargo"
                                                onClick={handleApplyPrice}
                                                loading={loading.applyPrice || loading.createDebt}
                                                disabled={(!selectedPriceSku && !isManualDebt) || (isManualDebt && (!manualDebtData.amount || !manualDebtData.description))}
                                            />
                                        </div>
                                    </div>

                                    {!(priceList || []).length && !isManualDebt && (
                                        <div className="help-block m-t-sm">
                                            <span className="text-muted">No hay plantillas configuradas para este torneo.</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : (
                        /* Step 2: Payment Registration */
                        <div>
                            <div className="m-b-md">
                                <h4 style={{ fontWeight: 'bold' }}>
                                    {paymentMode === "invoices" ? "Registrar Pago de Facturas" : "Registrar Carga de Saldo"}
                                </h4>
                                <p className="text-muted">
                                    {paymentMode === "invoices"
                                        ? `Estás registrando el pago de ${selectedInvoiceIds.length} facturas.`
                                        : "Registra una carga de saldo (top-up) para la cuenta del equipo."}
                                </p>
                            </div>

                            {paymentMode === "invoices" ? (
                                <div className="alert alert-info" style={{ borderRadius: '10px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span>Total a cobrar por facturas:</span>
                                        <h3 className="no-margins" style={{ fontWeight: 'bold' }}>
                                            $ {formatCurrency(invoices
                                                .filter(inv => selectedInvoiceIds.includes(inv.id))
                                                .reduce((sum, inv) => sum + inv.final_amount, 0)
                                            )}
                                        </h3>
                                    </div>
                                </div>
                            ) : (
                                <div className="form-group">
                                    <label>Monto a cargar ($)</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        placeholder="0.00"
                                        value={balanceAmount}
                                        onChange={(e) => setBalanceAmount(e.target.value)}
                                        style={{ backgroundColor: '#f3f3f4', border: 'none', height: '45px', fontSize: '18px', fontWeight: 'bold' }}
                                    />
                                    <label className="m-t-md">Descripción / Motivo</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Ej: Pago adelantado para próximo torneo"
                                        value={balanceDescription}
                                        onChange={(e) => setBalanceDescription(e.target.value)}
                                        style={{ backgroundColor: '#f3f3f4', border: 'none' }}
                                    />
                                </div>
                            )}

                            <div className="form-group m-t-md">
                                <label>Método de pago</label>
                                <select
                                    className="form-control"
                                    value={paymentMethod}
                                    onChange={(e) => setPaymentMethod(e.target.value)}
                                    style={{ backgroundColor: '#f3f3f4', border: 'none' }}
                                >
                                    <option value="cash">Efectivo</option>
                                    <option value="transfer">Transferencia</option>
                                    <option value="card">Tarjeta / Link de pago</option>
                                    <option value="other">Otro</option>
                                </select>
                            </div>

                            <div className="text-center m-t-lg">
                                <Button
                                    className="btn btn-primary btn-lg"
                                    title={paymentMode === "invoices" ? "Registrar Pago" : "Cargar Saldo"}
                                    onClick={handleRegisterPayment}
                                    loading={loading.registerPayment || loading.registerBalance}
                                    style={{ borderRadius: '25px', padding: '10px 40px', fontWeight: 'bold', backgroundColor: '#1ab394', border: 'none' }}
                                    disabled={paymentMode === "balance" && !balanceAmount}
                                />
                            </div>
                        </div>
                    )
                    }
                </div >
                </LoadingOverlay>
            }
        />
    );
};

export default FinancialModal;
